import React, { useState, useEffect, useRef } from 'react';
import { useTheme, createThemedStyles } from '../../contexts/ThemeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCheck } from '@/icons';
import type { BaseCalculatorProps } from './types';

export const AnnualCalculator: React.FC<BaseCalculatorProps> = ({
    currentValue,
    unit,
    decimalPlaces,
    onSave,
    onClose,
}) => {
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);
    const monthlyInputRef = useRef<HTMLInputElement>(null);
    const [monthlyInput, setMonthlyInput] = useState('');
    const [annualResult, setAnnualResult] = useState(0);

    useEffect(() => {
        if (currentValue && !isNaN(parseFloat(currentValue)) && parseFloat(currentValue) > 0) {
            const value = parseFloat(currentValue);
            if (unit === 'month') {
                setMonthlyInput(value.toString());
                setAnnualResult(value * 12);
            } else {
                const monthly = value / 12;
                setMonthlyInput(monthly.toFixed(2));
                setAnnualResult(value);
            }
        } else {
            setMonthlyInput('');
            setAnnualResult(0);
        }
    }, [currentValue, unit]);

    useEffect(() => {
        monthlyInputRef.current?.focus();
    }, []);

    useEffect(() => {
        const monthly = parseFloat(monthlyInput) || 0;
        setAnnualResult(monthly * 12);
    }, [monthlyInput]);

    const handleSave = () => {
        const monthly = parseFloat(monthlyInput) || 0;
        const amount = monthly * 12;
        const rounded = Math.round(amount * Math.pow(10, decimalPlaces)) / Math.pow(10, decimalPlaces);
        onSave(rounded);
        onClose();
    };

    return (
        <>
            <div style={{
                backgroundColor: '#dbeafe',
                border: '1px solid #93c5fd',
                borderRadius: theme.borderRadius.sm,
                padding: theme.spacing.md,
                marginBottom: theme.spacing.lg,
                display: 'flex',
                alignItems: 'flex-start',
                gap: theme.spacing.sm,
            }}>
                <i className="fas fa-info-circle" style={{ color: '#1e40af', fontSize: theme.typography.fontSize.md, marginTop: '2px' }} />
                <p style={{ margin: 0, fontSize: theme.typography.fontSize.sm, color: '#1e40af', lineHeight: 1.5 }}>
                    Enter the monthly amount and Calculist will calculate the annual equivalent for you.
                </p>
            </div>
            <div style={{ marginBottom: theme.spacing.lg }}>
                <label style={{ display: 'block', fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.bold, color: theme.colors.text.primary, marginBottom: theme.spacing.sm }}>Monthly Amount:</label>
                <input
                    ref={monthlyInputRef}
                    type="number"
                    value={monthlyInput}
                    onChange={(e) => setMonthlyInput(e.target.value)}
                    style={{ width: '100%', padding: theme.spacing.sm, border: `1px solid ${theme.colors.border.default}`, borderRadius: theme.borderRadius.sm, fontSize: theme.typography.fontSize.sm }}
                    placeholder="0.00"
                    step="0.01"
                />
            </div>
            <div style={{ marginBottom: theme.spacing.xl }}>
                <label style={{ display: 'block', fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.bold, color: theme.colors.text.primary, marginBottom: theme.spacing.sm }}>Annual Amount:</label>
                <div style={{ padding: theme.spacing.sm, backgroundColor: theme.colors.surfaceSecondary, border: `1px solid ${theme.colors.border.default}`, borderRadius: theme.borderRadius.sm, fontSize: theme.typography.fontSize.md, fontWeight: theme.typography.fontWeight.medium, color: theme.colors.text.primary }}>
                    ${annualResult.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing.sm }}>
                <button onClick={onClose} style={{ ...themedStyles.button.primary, backgroundColor: 'transparent', color: theme.colors.text.primary, border: `1px solid ${theme.colors.border.default}`, padding: `${theme.spacing.sm} ${theme.spacing.lg}`, display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                    <FontAwesomeIcon icon={faTimes} /> Cancel
                </button>
                <button onClick={handleSave} style={{ ...themedStyles.button.primary, backgroundColor: theme.colors.primary, color: theme.colors.text.inverse, padding: `${theme.spacing.sm} ${theme.spacing.lg}`, display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                    <FontAwesomeIcon icon={faCheck} /> Apply
                </button>
            </div>
        </>
    );
};
